import React from "react";
import { Avatar, Box, Chip, Stack, Tooltip, Typography } from "@mui/material";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";

import { ds } from "../../styles/designSystem";
import ComboBox from "../../component/common/ComboBox";
import {
    FEEDBACK_STATUS_ORDER,
    formatDateTime,
    formatRelativeTime,
    statusMeta,
} from "./feedbackConstants";

const C = ds.colors;

/**
 * 익명 아바타. 작성자는 관리자 포함 모두 <b>항상 익명</b>으로 표시한다(이름 노출 안 함).
 */
export function AnonAvatar({ author, size = 40 }) {
    const revealed = false; // 항상 익명 — 작성자 이름 노출 안 함
    const initial = revealed && author?.name ? author.name.trim().charAt(0) : null;
    return (
        <Avatar
            sx={{
                width: size,
                height: size,
                fontSize: size * 0.4,
                fontWeight: 700,
                bgcolor: revealed ? C.primaryLight : "#e2e8f0",
                color: revealed ? C.primaryText : C.textMuted,
            }}
        >
            {initial || <PersonOutlineRoundedIcon sx={{ fontSize: size * 0.55 }} />}
        </Avatar>
    );
}

/**
 * 작성자 이름 줄 — 관리자 포함 모두 <b>항상 "익명"</b>(이름·부서·직책 비노출). 본인 글이면 "내 글" 배지.
 */
export function AuthorLine({ author, mine, createdAt, updatedAt, mineLabel = "내 글" }) {
    const revealed = false; // 항상 익명 — 이름·부서·직책 노출 안 함
    const meta = revealed
        ? [author.deptName, author.positionName].filter(Boolean).join(" · ")
        : "";
    return (
        <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" sx={{ rowGap: 0.25 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 14, color: C.textPrimary }}>
                {revealed ? author.name : "익명"}
            </Typography>
            {mine && (
                <Chip
                    label={mineLabel}
                    size="small"
                    sx={{
                        height: 18,
                        fontSize: 11,
                        fontWeight: 700,
                        bgcolor: C.primaryBg,
                        color: C.primary,
                        border: `1px solid ${C.primaryLight}`,
                        "& .MuiChip-label": { px: 0.7 },
                    }}
                />
            )}
            <Typography sx={{ fontSize: 12, color: C.textMuted }} noWrap>
                {meta && `${meta} · `}
                <Tooltip title={formatDateTime(createdAt)}>
                    <Box component="span">{formatRelativeTime(createdAt)}</Box>
                </Tooltip>
                {updatedAt ? " (수정됨)" : ""}
            </Typography>
        </Stack>
    );
}

/** 상태 칩 (검토중/진행중/부분 완료/완료/보류·거절). */
export function StatusChip({ status, size = "small" }) {
    const m = statusMeta(status);
    return (
        <Chip
            label={m.label}
            size={size}
            sx={{ bgcolor: m.bg, color: m.color, fontWeight: 700 }}
        />
    );
}

/**
 * 관리자용 상태 선택 셀렉트. onChange(nextStatus) 로 상위에 위임.
 * 카드/모달 어디에 놓아도 부모 클릭(상세 열기) 이 트리거되지 않도록 클릭 전파를 막는다.
 */
export function StatusSelect({ value, onChange, disabled = false }) {
    // 공통 ComboBox 사용(시스템 표준 드롭다운). 카드 안에 놓여도 부모 클릭(상세 열기)이 트리거되지 않게 래핑.
    return (
        <Box onClick={(e) => e.stopPropagation()} sx={{ minWidth: 132 }}>
            <ComboBox
                clearable={false}
                disabled={disabled}
                options={FEEDBACK_STATUS_ORDER.map((s) => ({ value: s, label: statusMeta(s).label }))}
                value={value}
                onChange={(v) => onChange?.(v)}
                sx={{ "& .MuiInputBase-root": { height: 32, fontSize: 12, fontWeight: 700 } }}
            />
        </Box>
    );
}
